/* The wallet pair (batch 16, 18/09). Until now every « Ajouter au Wallet » in the product said
   Google — written when Google was the only one live (demo mode verified 28/08) and Apple's
   enrolment was still in the queue. Apple's enrolment was ACCEPTED 09/09, so the product needs
   both names in every place a pass is offered.

   Two rules keep this honest:

   ① ONE PLACE. Every button label, every failure line and the order the wallets appear in comes
      from here — not restated in Journey, Mes cadeaux or anywhere else. Adding a third wallet,
      or renaming one, is an edit to this file.

   ② APPLE STAYS OFF UNTIL IT CAN ACTUALLY ISSUE. A Pass Type ID certificate does not exist yet
      (the owner's licence + 99 USD, then the CSR). A button that offers a pass we cannot sign is
      a promise we break in front of the customer, so `appleReady()` gates it — false by default.
      When Technical lands the certificate, one flag flips and Apple appears everywhere at once.
      Nothing else has to change.

   The platform ordering is the customer's, not ours: an Android phone is never offered an Apple
   pass, and an iPhone is offered Apple first — though Google Wallet's iOS app works and stays
   as the second option (verified on iPhone 28/08, contrary to what we had assumed). */
import { useSyncExternalStore } from 'react'
import { currentWorld } from '@/mocks/worlds'

export type WalletId = 'google' | 'apple'

export interface Wallet {
  id: WalletId
  /** The wallet's own name — Apple's and Google's brand guidelines both require it in full. */
  name: string
  /** The button. */
  add: string
  /** The button after a failure. */
  retry: string
}

export const WALLETS: Record<WalletId, Wallet> = {
  apple: {
    id: 'apple',
    name: 'Apple Wallet',
    add: 'Ajouter à Apple Wallet',
    retry: 'Réessayer l’ajout à Apple Wallet',
  },
  google: {
    id: 'google',
    name: 'Google Wallet',
    add: 'Ajouter à Google Wallet',
    retry: 'Réessayer l’ajout à Google Wallet',
  },
}

/** The line shown when a pass cannot be added. The wallet is named — « le Wallet » told the
    customer nothing, and a customer who sees the wrong brand name stops trusting the screen. */
export const failLine = (w: Wallet) =>
  `${w.name} ne répond pas pour le moment. Votre cadeau reste utilisable ici — présentez simplement ce code en caisse.`

/* ---------- the platform ---------- */

export type Platform = 'ios' | 'mac' | 'android' | 'other'

/** Which device the customer is holding. iPadOS 13+ reports itself as a Mac, so the touch test
    is what separates an iPad from a desktop — without it every iPad is offered a desktop flow. */
export function platform(): Platform {
  try {
    const ua = navigator.userAgent || ''
    if (/Android/i.test(ua)) return 'android'
    if (/iPhone|iPod/i.test(ua)) return 'ios'
    if (/iPad/i.test(ua)) return 'ios'
    if (/Macintosh|Mac OS X/i.test(ua)) return navigator.maxTouchPoints > 1 ? 'ios' : 'mac'
    return 'other'
  } catch { return 'other' }
}

/** The order for a platform, before the readiness gate. Android and Windows never see Apple —
    a .pkpass is inert there, and offering it is a dead end dressed as a choice. */
function orderFor(p: Platform): WalletId[] {
  if (p === 'android' || p === 'other') return ['google']
  return ['apple', 'google']   // ios · mac
}

/* ---------- is Apple able to issue yet? ---------- */

const appleKey = () => `bmr.wallet-apple.${currentWorld()}`
const listeners = new Set<() => void>()
const notify = () => listeners.forEach(l => l())

/** True once a Pass Type ID certificate exists and the Worker can sign `.pkpass` files.
    TRUE by default since 07/10: the certificate exists (D-79) and the staging Worker signs
    real passes, proven on an iPhone the same day. The stored flag remains as an off switch. */
export function appleReady(): boolean {
  try { return localStorage.getItem(appleKey()) !== '0' } catch { return true }
}

export function setAppleReady(on: boolean): void {
  try { if (on) localStorage.removeItem(appleKey()); else localStorage.setItem(appleKey(), '0') } catch { /* fine */ }
  notify()
}

/* ---------- what to render ---------- */

/* useSyncExternalStore compares snapshots by identity, so a fresh array every call spins for
   ever (the lesson `getIdentityFields` taught in batch 15). Cache on the inputs. */
let cacheKey = ''
let cached: Wallet[] = []

/** The wallets to offer, in order, for this device and this readiness state.
    `?wallet=apple|google|both` on the URL overrides it — the review hook, so the pair can be
    seen at 390 and 1440 before Apple is genuinely live. */
export function wallets(): Wallet[] {
  let forced = ''
  try { forced = new URLSearchParams(location.search).get('wallet') || '' } catch { /* fine */ }
  const p = platform()
  const ready = appleReady()
  const key = `${forced}|${p}|${ready}`
  if (key === cacheKey) return cached

  let ids: WalletId[]
  if (forced === 'both') ids = ['apple', 'google']
  else if (forced === 'apple' || forced === 'google') ids = [forced as WalletId]
  else ids = orderFor(p).filter(id => id !== 'apple' || ready)

  cacheKey = key
  cached = ids.map(id => WALLETS[id])
  return cached
}

export function useWallets(): Wallet[] {
  return useSyncExternalStore(
    cb => { listeners.add(cb); return () => listeners.delete(cb) },
    wallets,
    wallets,
  )
}
